import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { sortBy } from "lodash";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { calculateDuration, getServiceDefaults } from "~/utils/visitsHelper";
import CheckboxSelect from "~/components/common/forms/CheckboxSelect";
import CareRequirementsEditor from "~/components/patient/CareRequirementsEditor";
import { getCareRequirementDetailedSummaryLabel } from "~/components/patient/careRequirementSummary";
import {
  CareRequirement,
  careRequirementToClinicalConstraintTemplateRequirementInput,
  cloneCareRequirement,
  createNewCareRequirement,
  getDefaultCareRequirementDateRange,
} from "@caredna-marlin/utils/client";
import { useCreateClinicalConstraintTemplateMutation } from "~/graphql/__generated__/graphql";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";

type Props = {
  open: boolean;
  onClose: () => void;
};

const TEMPLATE_FREQUENCY: CareRequirement["rruleFreq"] = "daily";

export default function CreateClinicalConstraintTemplateDialog({ open, onClose }: Props) {
  const orgConfig = useOrgConfig();
  const navigate = useNavigate();
  const { timeLabel } = useZonedDateFormat();

  const [createTemplate, { loading: creating }] = useCreateClinicalConstraintTemplateMutation({
    awaitRefetchQueries: true,
    refetchQueries: ["GetClinicalConstraintTemplatesForConfig"],
  });

  const serviceLineOptions = useMemo(() => {
    return sortBy(
      Object.values(orgConfig.serviceLines)
        .filter((sl): sl is NonNullable<typeof sl> => !!sl)
        .map((sl) => ({ id: sl.id, name: sl.name })),
      (sl) => sl.name ?? ""
    );
  }, [orgConfig.serviceLines]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [serviceLineId, setServiceLineId] = useState<number>(0);
  const [durationDays, setDurationDays] = useState<number>(7);
  const [careRequirements, setCareRequirements] = useState<CareRequirement[]>([]);
  const [firstVisitTypeId, setFirstVisitTypeId] = useState<number | null>(null);
  const [firstVisitServiceIds, setFirstVisitServiceIds] = useState<number[]>([]);
  const [firstVisitDuration, setFirstVisitDuration] = useState<number | null>(null);

  // Visit types selectable as the first / start-of-care visit for the chosen
  // service line.
  const firstVisitTypeOptions = useMemo(() => {
    const serviceLine = orgConfig.serviceLines[serviceLineId];
    return sortBy(
      (serviceLine?.visitTypeServiceLines ?? [])
        .map((vtsl) => orgConfig.visitTypes[vtsl.visitTypeId])
        .filter((vt): vt is NonNullable<typeof vt> => !!vt)
        .map((vt) => ({ id: vt.id, name: vt.name })),
      (vt) => vt.name ?? ""
    );
  }, [orgConfig.serviceLines, orgConfig.visitTypes, serviceLineId]);

  // Services associated with the selected first visit type. The first-visit
  // services picker is only shown when the chosen visit type has services.
  const firstVisitServiceOptions = useMemo(() => {
    if (firstVisitTypeId == null) { return []; }
    const visitType = orgConfig.visitTypes[firstVisitTypeId];
    return (visitType?.visitTypeServices ?? [])
      .map(({ service }) => service)
      .filter((service): service is NonNullable<typeof service> => !!service)
      .map((service) => ({ key: service.id, label: service.name ?? "" }));
  }, [orgConfig.visitTypes, firstVisitTypeId]);

  const resetState = () => {
    setName("");
    setDescription("");
    setServiceLineId(0);
    setDurationDays(7);
    setCareRequirements([]);
    setFirstVisitTypeId(null);
    setFirstVisitServiceIds([]);
    setFirstVisitDuration(null);
  };

  const close = () => {
    resetState();
    onClose();
  };

  const hasInvalidRows = careRequirements.some((cr) => !cr.visitTypeId);
  const canCreate = !!name.trim() && serviceLineId !== 0 && !hasInvalidRows && !creating;

  const onCreate = async () => {
    if (!canCreate) { return; }

    const requirements = careRequirements.map((cr) =>
      careRequirementToClinicalConstraintTemplateRequirementInput(cr)
    );

    const result = await createTemplate({
      variables: {
        serviceLineId,
        name: name.trim(),
        description: description.trim() || null,
        rruleFreq: TEMPLATE_FREQUENCY,
        durationDays,
        firstVisitTypeId,
        firstVisitServiceIds,
        firstVisitDuration,
        requirements,
      },
    });

    const newId = result.data?.createClinicalConstraintTemplate?.id;
    close();
    if (newId) {
      navigate(`/operate-config/clinical-templates/${newId}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth={false}
      slotProps={{ paper: { sx: { width: "100%", maxWidth: 960 }}}}
    >
      <DialogTitle>Create Clinical Template</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
          <Grid size={12}>
            <TextField
              label="Template name"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={creating}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              label="Description (optional)"
              fullWidth
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={creating}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={serviceLineId}
                renderValue={(selected) => {
                  if (selected === 0) {
                    return (
                      <Box component="span" sx={{ color: "text.disabled" }}>
                        Select service line
                      </Box>
                    );
                  }
                  return serviceLineOptions.find((sl) => sl.id === selected)?.name ?? selected;
                }}
                onChange={(e) => {
                  const value = e.target.value as number | string;
                  const nextId = typeof value === "number" ? value : parseInt(String(value), 10);
                  if (!Number.isFinite(nextId)) { return; }
                  setServiceLineId(nextId);
                  setCareRequirements([]);
                  setFirstVisitTypeId(null);
                  setFirstVisitServiceIds([]);
                  setFirstVisitDuration(null);
                }}
                disabled={creating}
              >
                <MenuItem value={0} disabled>
                  Service line
                </MenuItem>
                {serviceLineOptions.map((sl) => (
                  <MenuItem key={sl.id} value={sl.id}>
                    {sl.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              label="Duration (days)"
              type="number"
              fullWidth
              size="small"
              value={durationDays}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (Number.isFinite(value) && value >= 1) {
                  setDurationDays(value);
                }
              }}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={creating}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={firstVisitTypeId ?? 0}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Box component="span" sx={{ color: "text.disabled" }}>
                        First visit type
                      </Box>
                    );
                  }
                  return firstVisitTypeOptions.find((vt) => vt.id === selected)?.name ?? selected;
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  const nextId = Number.isFinite(value) && value !== 0 ? value : null;
                  setFirstVisitTypeId(nextId);
                  // Seed the visit type's default services and its default
                  // (services + resource requirement) duration, mirroring how
                  // selecting a visit type behaves elsewhere in the app.
                  if (nextId == null) {
                    setFirstVisitServiceIds([]);
                    setFirstVisitDuration(null);
                  } else {
                    const defaultServiceIds = getServiceDefaults(orgConfig, nextId);
                    setFirstVisitServiceIds(defaultServiceIds.map(Number));
                    setFirstVisitDuration(Number(calculateDuration(orgConfig, nextId, defaultServiceIds)));
                  }
                }}
                disabled={creating || serviceLineId === 0}
              >
                <MenuItem value={0}>
                  <em>None</em>
                </MenuItem>
                {firstVisitTypeOptions.map((vt) => (
                  <MenuItem key={vt.id} value={vt.id}>
                    {vt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {firstVisitServiceOptions.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth size="small">
                <CheckboxSelect
                  value={firstVisitServiceIds}
                  onChange={(serviceIds) => {
                    setFirstVisitServiceIds(serviceIds);
                    // Keep the duration in sync with the selected services'
                    // durations (plus the visit type's resource requirements).
                    if (firstVisitTypeId != null) {
                      setFirstVisitDuration(
                        Number(calculateDuration(orgConfig, firstVisitTypeId, serviceIds.map(String)))
                      );
                    }
                  }}
                  options={firstVisitServiceOptions}
                  fullWidth
                  size="small"
                />
              </FormControl>
            </Grid>
          )}

          {firstVisitTypeId != null && (
            <Grid size={12}>
              <TextField
                label="First visit duration (min)"
                type="number"
                fullWidth
                size="small"
                value={firstVisitDuration ?? ""}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  setFirstVisitDuration(Number.isFinite(value) && value >= 0 ? value : null);
                }}
                slotProps={{ htmlInput: { min: 0 } }}
                disabled={creating}
              />
            </Grid>
          )}

          <Grid size={12}>
            <CareRequirementsEditor
              careRequirements={careRequirements}
              setCareRequirements={setCareRequirements}
              serviceLineId={serviceLineId}
              createNewRequirement={() => createNewCareRequirement({
                frequency: TEMPLATE_FREQUENCY,
                ...getDefaultCareRequirementDateRange(),
              })}
              duplicateRequirement={(requirement) => cloneCareRequirement(requirement)}
              summaryLabel={(careRequirement, index) => getCareRequirementDetailedSummaryLabel({
                careRequirement,
                index,
                visitTypeName: orgConfig.visitTypes[careRequirement.visitTypeId]?.name,
                timeLabel,
              })}
              addDisabled={serviceLineId === 0 || creating}
              mode="openNewRows"
              contentPaddingX={1}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={creating}>
          Cancel
        </Button>
        <Button
          onClick={onCreate}
          disabled={!canCreate}
          loading={creating}
          variant="contained"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
